import { useRef } from "react";
import { makeStyles } from "@material-ui/styles";
import Box from "../../Common/Box/Box";
import Chart from "../../Common/Box/Chart";
import { chartDownloadImageScaleFactor } from "../../../constants";
import initialData from "../../../data/initialData.json";
import exportCharts from "../../../export";
import useCsvDownload from "../../../hooks/useCsvDownload";
import { getTooltip } from "../../../utilities";

const key = "FP_f10";
const number = 10;

const segmentColors = [
  "#de6746",
  "#f37447",
  "#f69877",
  "#f8b096",
  "#fddfd3",
  "#bfbfbf",
];

const labels = {
  lowIncome: [
    {
      text: "Ethiopia",
      x: 218,
      y: 115,
      color: "black",
    },
    {
      text: "Yemen",
      x: 250,
      y: 190,
      color: "black",
    },
    {
      text: "Syrian Arab Republic",
      x: 270,
      y: 290,
      color: "black",
    },
    {
      text: "Uganda",
      x: 195,
      y: 320,
      color: "black",
    },
    {
      text: "Sudan",
      x: 155,
      y: 290,
      color: "black",
    },
    {
      text: "Remaining<br />countries",
      x: 95,
      y: 170,
      color: "black",
    },
  ],
  lowMiddleIncome: [
    {
      text: "India",
      x: 235,
      y: 140,
      color: "black",
    },
    {
      text: "Bangladesh",
      x: 238,
      y: 220,
      color: "black",
    },
    {
      text: "Philippines",
      x: 250,
      y: 305,
      color: "black",
    },
    {
      text: "Morocco",
      x: 169,
      y: 295,
      color: "black",
    },
    {
      text: "Algeria",
      x: 125,
      y: 280,
      color: "black",
    },
    {
      text: "Remaining<br />countries",
      x: 95,
      y: 160,
      color: "black",
    },
  ],
  upperMiddleIncome: [
    {
      text: "Brazil",
      x: 240,
      y: 160,
      color: "black",
    },
    {
      text: "China",
      x: 217,
      y: 263,
      color: "black",
    },
    {
      text: "Russia",
      x: 147,
      y: 290,
      color: "black",
    },
    {
      text: "Argentina",
      x: 62,
      y: 294,
      color: "black",
    },
    {
      text: "Mexico",
      x: 84,
      y: 240,
      color: "black",
    },
    {
      text: "Remaining<br />countries",
      x: 100,
      y: 140,
      color: "black",
    },
  ],
};

const options = (data, labels) => (theme) => ({
  chart: {
    type: "pie",
    backgroundColor: null,
    height: 350,
    width: 380,
    events: {
      render: function () {
        const chart = this;

        for (let label of labels) {
          chart.renderer
            .label(label.text, label.x, label.y, null, null, null, true)
            .css({ color: label.color, textAlign: "center" })
            .attr({
              zIndex: 6,
            })
            .add();
        }
      },
    },
  },
  title: {
    text: data[0].name,
    align: "center",
  },
  plotOptions: {
    pie: {
      size: "95%",
      dataLabels: {
        enabled: false,
        distance: 10,
        style: {
          fontWeight: "normal",
        },
      },
    },
  },
  tooltip: {
    backgroundColor: "white",
    useHTML: true,
    outside: true,
    formatter: function () {
      return getTooltip("Cost", this.y, this.percentage, {
        prefix: "$",
      });
    },
    style: {
      fontSize: "0.875rem",
      color: theme.palette.tooltip.primary,
    },
  },
  exporting: {
    chartOptions: {
      title: {
        text: data[0].name,
      },
    },
    enabled: false,
  },
  series: [
    {
      name: data[0].name,

      data: data[0].data.map((item, i) => ({
        ...item,
        color: segmentColors[i],
      })),
    },
  ],
});

const useStyles = makeStyles((theme) => ({
  chart: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  facetPanel: {
    display: "flex",
    justifyContent: "space-evenly",
  },
  title: {
    color: theme.palette.title.chart,
    fontSize: "1.25rem",
  },
}));

const Component = () => {
  const highchartRefs = [useRef(), useRef(), useRef()];

  const downloadCsv = useCsvDownload();

  const classes = useStyles();

  const handleCsvDownload = () => {
    downloadCsv("/fp/figures/10/csv", `fp_f${number}.csv`);
  };

  const data = initialData["fp_f10"];

  return (
    <Box
      key={key}
      caption={`Figure ${number}`}
      title="Contraceptive Supply Costs by Method"
      subtitle="Top Five Countries in Each Income Group, 2019"
      onDownloadCsv={handleCsvDownload}
      onDownloadPng={() => {
        exportCharts(
          {
            chart1: highchartRefs[0].current,
            chart2: highchartRefs[1].current,
            chart3: highchartRefs[2].current,
          },
          {
            filename: key,
            scale: chartDownloadImageScaleFactor,
          }
        );
      }}
    >
      <div className={classes.facetPanel}>
        <Chart
          highchartRef={highchartRefs[0]}
          options={options(data["fp_f10_a"], labels.lowIncome)}
        />
        <Chart
          highchartRef={highchartRefs[1]}
          options={options(data["fp_f10_b"], labels.lowMiddleIncome)}
        />
        <Chart
          highchartRef={highchartRefs[2]}
          options={options(data["fp_f10_c"], labels.upperMiddleIncome)}
        />
      </div>
    </Box>
  );
};

export default Component;
